import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import useOnCloseWithConfirmation from '@/hooks/useOnCloseWithConfirmation';
import { VideoFactorySettings } from '@/types/resources';
import { api } from '@/utils/api';
import { VideoFactorySettingsType } from '@/utils/enums';
import { handleFormErrors } from '@/utils/helpers';

const StyledSidePanel = styled(SidePanel)`
  width: 600px;
  textarea {
    min-height: 400px;
  }
`;

type FormDataT = {
  prompt: string;
};

interface Props {
  onClose: VoidFunction;
  setting?: VideoFactorySettings;
  defaultType: VideoFactorySettingsType;
  defaultCategoryId: number | null;
}

const EditForm: FC<Props> = ({
  onClose,
  setting,
  defaultType,
  defaultCategoryId,
}) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting, isDirty },
  } = useForm<FormDataT>({
    defaultValues: {
      prompt: setting?.data?.prompt ?? '',
    },
  });

  const onCancel = useOnCloseWithConfirmation(onClose, isDirty);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async (data) => {
      try {
        await api.post('/v1/video-factory/settings', {
          data,
          type: setting?.type ?? defaultType,
          category_id: defaultCategoryId,
        });

        await queryClient.invalidateQueries(['video_factory_settings']);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <StyledSidePanel heading={'Edit prompt'} onClose={onCancel}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                label="Prompt"
                type="textarea"
                name="prompt"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Save
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </StyledSidePanel>
    </Modal>
  );
};

export default EditForm;
